import { useState, useEffect, useMemo, useRef } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import moment from 'moment';
import { Button } from '@shadcn/ui';
import conedService from 'app/services/coned';
import { useConEdisonData } from 'app/hooks/useConEdisonData';
import { sharedXAxisProps, sharedTooltipProps } from 'app/components/charts/shared/chartProps';
import type { ConEdAnalysisProps, IntervalBlock, IntervalBlockParams } from './types';
import { toEasternTime, getPublishedDateRange, getCurrentEasternTime } from './utils';
import { TimeRange, TimeRangeConfig } from './types';
import { TIME_RANGE_CONFIG, INTERVAL_LABELS } from './constants';

interface ProcessedDataPoint {
  hour: string;
  value: number;
}

// Add new type for data mode
type DataMode = 'realtime' | 'historical';

const CombinedChartSkeleton = () => (
  <div className="rounded-lg border p-2 sm:p-4">
    <h4 className="mb-2 text-base font-medium sm:mb-4">Energy Usage</h4>
    <div className="h-[250px] rounded-lg bg-muted sm:h-[400px]"></div>
  </div>
);

const InsightsSkeleton = () => (
  <div className="space-y-6">
    {/* Usage Patterns Section Skeleton */}
    <div className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm">
      <h3 className="mb-4 text-lg font-semibold text-gray-800">Usage Patterns</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* Peak Period Card */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-white p-4">
          <div className="mb-2 flex items-center gap-2">
            <div className="rounded-full bg-blue-100 p-2">
              <div className="h-4 w-4 rounded-full bg-muted"></div>
            </div>
            <h4 className="text-sm font-medium text-gray-800">Peak Period</h4>
          </div>
          <div className="space-y-2">
            <div className="h-8 w-20 rounded-md bg-muted"></div>
            <div className="h-4 w-32 rounded-md bg-muted"></div>
          </div>
        </div>

        {/* Peak Time Card */}
        <div className="rounded-lg bg-gradient-to-br from-orange-50 to-white p-4">
          <div className="mb-2 flex items-center gap-2">
            <div className="rounded-full bg-orange-100 p-2">
              <div className="h-4 w-4 rounded-full bg-muted"></div>
            </div>
            <h4 className="text-sm font-medium text-gray-800">Peak Time</h4>
          </div>
          <div className="space-y-2">
            <div className="h-8 w-20 rounded-md bg-muted"></div>
            <div className="h-4 w-32 rounded-md bg-muted"></div>
          </div>
        </div>

        {/* Off-Peak Usage Card */}
        <div className="rounded-lg bg-gradient-to-br from-green-50 to-white p-4">
          <div className="mb-2 flex items-center gap-2">
            <div className="rounded-full bg-green-100 p-2">
              <div className="h-4 w-4 rounded-full bg-muted"></div>
            </div>
            <h4 className="text-sm font-medium text-gray-800">Off-Peak Usage</h4>
          </div>
          <div className="space-y-2">
            <div className="h-8 w-20 rounded-md bg-muted"></div>
            <div className="h-4 w-32 rounded-md bg-muted"></div>
          </div>
        </div>
      </div>
    </div>

    {/* Consumption Analysis Section Skeleton */}
    <div className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm">
      <h3 className="mb-4 text-lg font-semibold text-gray-800">Consumption Analysis</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Baseload Card */}
        <div className="rounded-lg bg-gradient-to-br from-purple-50 to-white p-4">
          <div className="flex items-center gap-2">
            <div className="rounded-full bg-purple-100 p-2">
              <div className="h-4 w-4 rounded-full bg-muted"></div>
            </div>
            <h4 className="text-sm font-medium text-gray-800">Baseload</h4>
          </div>
          <div className="mt-2">
            <div className="h-8 w-20 rounded-md bg-muted"></div>
            <div className="h-4 w-32 rounded-md bg-muted"></div>
            <p className="mt-2 text-xs text-gray-500">
              This represents your minimum continuous power usage, typically from always-on appliances and standby
              power.
            </p>
          </div>
        </div>

        {/* Peak vs Average Card */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-white p-4">
          <div className="mb-2 flex items-center gap-2">
            <div className="rounded-full bg-indigo-100 p-2">
              <div className="h-4 w-4 rounded-full bg-muted"></div>
            </div>
            <h4 className="text-sm font-medium text-gray-800">Peak vs Average</h4>
          </div>
          <div className="space-y-2">
            <div className="h-8 w-20 rounded-md bg-muted"></div>
            <div className="h-4 w-32 rounded-md bg-muted"></div>
            <p className="mt-2 text-xs text-gray-500">
              A high peak-to-average ratio indicates potential for energy savings through load shifting.
            </p>
          </div>
        </div>
      </div>
    </div>

    {/* Optimization Opportunities Section Skeleton */}
    <div className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm">
      <h3 className="mb-4 text-lg font-semibold text-gray-800">Optimization Opportunities</h3>
      <div className="rounded-lg bg-gradient-to-br from-teal-50 to-white p-4">
        <div className="mb-2 flex items-center gap-2">
          <div className="rounded-full bg-teal-100 p-2">
            <div className="h-4 w-4 rounded-full bg-muted"></div>
          </div>
          <h4 className="text-sm font-medium text-gray-800">Potential Savings</h4>
        </div>
        <div className="space-y-3">
          <div className="h-8 w-24 rounded-md bg-muted"></div>
          <div className="h-4 w-48 rounded-md bg-muted"></div>
          <div className="mt-4 space-y-2 rounded-lg bg-white/50 p-3">
            <h5 className="font-medium text-gray-700">Recommendations:</h5>
            <ul className="list-inside list-disc space-y-1 text-sm text-gray-600">
              <li>Consider scheduling high-energy activities during off-peak hours</li>
              <li>Use timers or smart plugs to automate device usage during off-peak periods</li>
              <li>Identify and minimize non-essential usage during peak hours</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const ConEdisonResidentialAnalysis = ({ project }: Omit<ConEdAnalysisProps, 'conEdCustomerMeters'>) => {
  const { customerMeters: conEdCustomerMeters, isLoading: isLoadingMeters } = useConEdisonData(project);
  const [baseloads, setBaseloads] = useState<{ [key: string]: number }>({});
  const [selectedMeterIds, setSelectedMeterIds] = useState<string[]>([]);
  const [intervalData, setIntervalData] = useState<{ [key: string]: IntervalBlock[] }>({});
  const [loading, setLoading] = useState(false);
  const [allMeterDataLoaded, setAllMeterDataLoaded] = useState(false);
  const [dataMode, setDataMode] = useState<DataMode>('realtime');

  const currentEastern = getCurrentEasternTime();

  // Set default dates to yesterday in Eastern time
  const yesterday = new Date(currentEastern.getTime() - 24 * 60 * 60 * 1000);
  const yesterdayStr = yesterday.toISOString().slice(0, 19).replace('T', ' ');
  const [startDate, setStartDate] = useState<string>(yesterdayStr);
  const [endDate, setEndDate] = useState<string>(yesterdayStr);
  const [dateError, setDateError] = useState<string>('');

  // Initialize time range
  const [timeRange, setTimeRange] = useState<TimeRange>('realtime');

  // Remove intervalType state and use computed value instead
  const intervalType = useMemo(() => {
    switch (timeRange) {
      case 'realtime':
        return '5min';
      case 'day':
        return '1hr';
      case 'week':
      case 'month':
        return '24hr';
      default:
        return '5min';
    }
  }, [timeRange]);

  // Update formatDateRange to use consistent timezone handling
  const formatDateRange = () => {
    const formatDate = (date: moment.Moment) => {
      return toEasternTime(date.toDate()).format('ddd, MMM D, h:mm A');
    };

    const now = toEasternTime(new Date());
    let end = moment.min(now, moment()) as moment.Moment;
    let start: moment.Moment;

    if (timeRange === 'day' || timeRange === 'realtime') {
      start = end.clone().subtract(24, 'hours');
    } else {
      start = end.clone().subtract(TIME_RANGE_CONFIG[timeRange].days - 1, 'days');
    }

    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  // Add abort controller ref
  const abortControllerRef = useRef<AbortController | null>(null);

  // Clear existing data and cancel pending requests when changing time range
  const handleTimeRangeChange = (newRange: TimeRange) => {
    // Cancel any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Clear existing data
    setIntervalData({});
    setBaseloads({});
    setAllMeterDataLoaded(false);
    setLoading(true);

    // Set new time range
    setTimeRange(newRange);
  };

  // Update time range effect
  useEffect(() => {
    const config = TIME_RANGE_CONFIG[timeRange];
    const now = toEasternTime(new Date());
    let end = moment.min(now, moment()) as moment.Moment;
    let start: moment.Moment;

    if (timeRange === 'day' || timeRange === 'realtime') {
      start = end.clone().subtract(24, 'hours');
    } else {
      start = end.clone().subtract(config.days - 1, 'days');
    }

    // Set data mode based on time range
    if (timeRange === 'realtime') {
      setDataMode('realtime');
    } else {
      setDataMode('historical');
    }

    // Convert to UTC for API requests
    setEndDate(moment(end.toDate()).utc().format('YYYY-MM-DD HH:mm:ss'));
    setStartDate(moment(start.toDate()).utc().format('YYYY-MM-DD HH:mm:ss'));
  }, [timeRange]);

  // Move allMeters calculation into useMemo to prevent recalculation on every render
  const allMeters = useMemo(
    () =>
      conEdCustomerMeters.flatMap((customerMeter) =>
        customerMeter.serviceLocations.flatMap((location) =>
          location.meters.map((meter) => ({
            serialNumber: meter.serialNumber,
            serviceLocationId: location.serviceLocationId,
            customerAgreementId: customerMeter.customerAgreementId,
            meterReadings: meter.meterReadings,
          }))
        )
      ),
    [conEdCustomerMeters]
  );

  // Generate unique colors for each meter
  const meterColors = useMemo(() => {
    const colors = [
      'hsl(var(--chart-1))',
      'hsl(var(--chart-2))',
      'hsl(var(--chart-3))',
      'hsl(var(--chart-4))',
      'hsl(var(--chart-5))',
      'rgb(var(--chart-energy-rgb))',
      'rgb(var(--chart-temperature-rgb))',
      'rgb(var(--chart-cooling-rgb))',
      'rgb(var(--chart-heating-rgb))',
    ];
    return Object.fromEntries(
      allMeters.map((meter, index) => [meter.serialNumber.toString(), colors[index % colors.length]])
    );
  }, [allMeters]);

  useEffect(() => {
    if (allMeters.length > 0 && selectedMeterIds.length === 0) {
      setSelectedMeterIds([allMeters[0].serialNumber.toString()]);
    }
  }, [allMeters, selectedMeterIds.length]);

  const validateDateRange = (start: string, end: string) => {
    if (!start || !end) return false;

    const startMoment = moment(start);
    const endMoment = moment(end);
    const thirtyDays = moment.duration(30, 'days');

    if (moment.duration(endMoment.diff(startMoment)).asMilliseconds() > thirtyDays.asMilliseconds()) {
      setDateError('Date range cannot exceed 30 days');
      return false;
    }

    if (startMoment.isAfter(endMoment)) {
      setDateError('Start date must be before end date');
      return false;
    }

    setDateError('');
    return true;
  };

  // Update data fetching effect with abort controller
  useEffect(() => {
    const fetchData = async () => {
      if (
        !startDate ||
        !endDate ||
        !validateDateRange(startDate, endDate) ||
        !project?.attributes?.conEdisonSubscriptionId ||
        allMeters.length === 0
      ) {
        setAllMeterDataLoaded(false);
        setLoading(false);
        return;
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      setLoading(true);
      setAllMeterDataLoaded(false);

      try {
        const newIntervalData: { [key: string]: IntervalBlock[] } = {};
        const newBaseloads: { [key: string]: number } = {};

        // For realtime, use getRealTimeIntervalBlocks
        if (dataMode === 'realtime') {
          for (const meter of allMeters) {
            if (signal.aborted) {
              throw new Error('Request aborted');
            }

            if (!meter.serviceLocationId) {
              console.error('Service location ID not found for meter:', meter.serialNumber);
              continue;
            }

            try {
              const response = await conedService.getRealTimeIntervalBlocks({
                subscriptionId: project.attributes.conEdisonSubscriptionId,
                usagePointId: meter.serviceLocationId,
                meterSerialNumber: meter.serialNumber.toString(),
              });

              if (signal.aborted) {
                throw new Error('Request aborted');
              }

              const transformedData = response.intervalBlocks.flatMap((block) =>
                block.IntervalBlock.readings.map((reading) => ({
                  startTime: toEasternTime(reading.timePeriod.start * 1000).toISOString(),
                  value: reading.value / 1000,
                  meterId: meter.serialNumber.toString(),
                }))
              );

              newIntervalData[meter.serialNumber.toString()] = transformedData;
              newBaseloads[meter.serialNumber.toString()] = response.baseload / 1000;
            } catch (error: unknown) {
              if (error instanceof Error && error.name === 'AbortError') {
                throw error;
              }
              console.error(`Error fetching data for meter ${meter.serialNumber}:`, error);
            }
          }
        } else {
          // Existing historical data fetching logic
          for (const meter of allMeters) {
            if (signal.aborted) {
              throw new Error('Request aborted');
            }

            if (!meter.serviceLocationId) {
              console.error('Service location ID not found for meter:', meter.serialNumber);
              continue;
            }

            const kwhReading = meter.meterReadings.find((reading) => reading.type === 'KWH');
            if (!kwhReading) {
              console.error('KWH reading not found for meter');
              continue;
            }

            const { publishedMin, publishedMax } = getPublishedDateRange(startDate, endDate);
            try {
              const response = await conedService.getIntervalBlocks({
                subscriptionId: project.attributes.conEdisonSubscriptionId,
                usagePointId: meter.serviceLocationId,
                meterReadingId: kwhReading.id,
                publishedMin,
                publishedMax,
                interval: intervalType === '24hr' ? 1440 : intervalType === '1hr' ? 60 : 5,
              } as IntervalBlockParams);

              if (signal.aborted) {
                throw new Error('Request aborted');
              }

              newBaseloads[meter.serialNumber.toString()] = response.baseload / 1000;
              const transformedData = response.intervalBlocks.flatMap((block) =>
                block.IntervalBlock.readings.map((reading) => ({
                  startTime: toEasternTime(reading.timePeriod.start * 1000).toISOString(),
                  value: reading.value / 1000,
                  meterId: meter.serialNumber.toString(),
                }))
              );
              newIntervalData[meter.serialNumber.toString()] = transformedData;
            } catch (error: unknown) {
              if (error instanceof Error && error.name === 'AbortError') {
                throw error;
              }
              console.error(`Error fetching data for meter ${meter.serialNumber}:`, error);
            }
          }
        }

        if (!signal.aborted) {
          setIntervalData(newIntervalData);
          setBaseloads(newBaseloads);
          setAllMeterDataLoaded(true);
        }
      } catch (error: unknown) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Error fetching interval data:', error);
          setIntervalData({});
          setBaseloads({});
          setAllMeterDataLoaded(false);
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    // Cleanup function to abort any pending requests when dependencies change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [startDate, endDate, allMeters, project?.attributes?.conEdisonSubscriptionId, intervalType, timeRange, dataMode]);

  // Reset allMeterDataLoaded when dependencies change
  useEffect(() => {
    setAllMeterDataLoaded(false);
  }, [startDate, endDate, intervalType]);

  const processIntervalData = (data: IntervalBlock[]): ProcessedDataPoint[] => {
    if (timeRange === 'month' || timeRange === 'week') {
      // Process data by days
      const dailyTotals: { [date: string]: number } = {};

      // Process each reading
      data.forEach((reading) => {
        const date = new Date(reading.startTime);
        const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
        const dateStr = `${dayName}, ${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`;
        if (!dailyTotals[dateStr]) {
          dailyTotals[dateStr] = 0;
        }
        dailyTotals[dateStr] += reading.value;
      });

      // Convert to the required format and sort by date
      return Object.entries(dailyTotals)
        .map(([dateStr, total]) => ({
          hour: dateStr,
          value: total,
        }))
        .sort((a, b) => {
          const [, aDateStr] = a.hour.split(', ');
          const [, bDateStr] = b.hour.split(', ');
          const [aMonth, aDay] = aDateStr.split('/').map(Number);
          const [bMonth, bDay] = bDateStr.split('/').map(Number);
          const currentYear = new Date().getFullYear();
          const dateA = new Date(currentYear, aMonth - 1, aDay);
          const dateB = new Date(currentYear, bMonth - 1, bDay);
          return dateA.getTime() - dateB.getTime();
        });
    } else {
      // For realtime and day views, process data respecting the 24-hour window
      const hourlyData = new Map<string, number>();

      if (data.length === 0) return [];

      // Sort data by timestamp
      const sortedData = [...data].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

      // Get the time range
      const endTime = new Date(sortedData[sortedData.length - 1].startTime);
      const startTime = new Date(endTime.getTime() - 24 * 60 * 60 * 1000);

      // Create a consistent key format for hours
      const formatHourKey = (date: Date) => {
        const easternDate = toEasternTime(date);
        const year = easternDate.year();
        const month = easternDate.month();
        const day = easternDate.date();
        const hour = easternDate.hour();
        return `${year}-${month}-${day}-${hour}`;
      };

      // Initialize all hours in the 24-hour window
      let currentHour = new Date(startTime);
      while (currentHour <= endTime) {
        const key = formatHourKey(currentHour);
        hourlyData.set(key, 0);
        currentHour = new Date(currentHour.getTime() + 60 * 60 * 1000);
      }

      // Aggregate data into hourly buckets
      data.forEach((reading) => {
        const readingTime = new Date(reading.startTime);
        if (readingTime >= startTime && readingTime <= endTime) {
          const hourKey = formatHourKey(readingTime);
          hourlyData.set(hourKey, (hourlyData.get(hourKey) || 0) + reading.value);
        }
      });

      // Convert to the required format
      const processedData: ProcessedDataPoint[] = Array.from(hourlyData.entries())
        .map(([key, total]) => {
          const [year, month, day, hour] = key.split('-').map(Number);
          const date = new Date(year, month, day, hour);
          const hourStr = hour === 0 ? '12 AM' : hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`;

          return {
            hour: hourStr,
            value: total,
            timestamp: date.toISOString(), // Keep the timestamp for sorting
          };
        })
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
        .map(({ hour, value }) => ({ hour, value })); // Remove timestamp from final output

      return processedData;
    }
  };

  const processIntervalDataByPeriod = (data: IntervalBlock[]): ProcessedDataPoint[] => {
    // Initialize period containers with zero values
    const periodData: { [key: string]: number } = {
      morning: 0, // 5:00 AM - 11:59 AM
      afternoon: 0, // 12:00 PM - 4:59 PM
      evening: 0, // 5:00 PM - 9:59 PM
      night: 0, // 10:00 PM - 4:59 AM
    };

    // Process each reading
    data.forEach((reading) => {
      const date = new Date(reading.startTime);
      const hour = date.getHours();
      const value = reading.value;

      // Determine which period this reading belongs to
      let period;
      if (hour >= 5 && hour < 12) {
        period = 'morning';
      } else if (hour >= 12 && hour < 17) {
        period = 'afternoon';
      } else if (hour >= 17 && hour < 22) {
        period = 'evening';
      } else {
        period = 'night';
      }

      // Add the value to the appropriate period
      periodData[period] += value;
    });

    // Format for chart
    return Object.entries(periodData).map(([period, total]) => ({
      hour: period.charAt(0).toUpperCase() + period.slice(1),
      value: total, // Total kWh for the period
    }));
  };

  const renderHourlyChart = (selectedMeterData: { data: ProcessedDataPoint[]; color: string; meterId: string }[]) => {
    const CustomTick = (props: any) => {
      const { x, y, payload } = props;
      return (
        <g transform={`translate(${x},${y})`}>
          <text x={0} y={0} dy={16} textAnchor="middle" fill="#666" fontSize={10} transform={`rotate(-45, 0, 0)`}>
            {payload.value}
          </text>
        </g>
      );
    };

    // Function to determine interval based on screen width and data length
    const getResponsiveInterval = () => {
      // Check if window is available (client-side)
      if (typeof window !== 'undefined') {
        const isMobile = window.innerWidth < 640; // sm breakpoint in Tailwind
        const dataLength = selectedMeterData[0]?.data?.length || 0;

        if (isMobile) {
          if (dataLength > 24) {
            return 5; // Show every 5th label on mobile for large datasets
          }
          return 2; // Show every other label on mobile for smaller datasets
        }
      }
      return 0; // Show all labels on desktop
    };

    return (
      <div className="flex-none rounded-lg border p-2 sm:p-4">
        <h4 className="mb-2 text-base font-medium sm:mb-4">Energy Usage</h4>
        <div className="h-[250px] sm:h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={selectedMeterData[0]?.data || []} margin={{ top: 20, right: 20, left: 40, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                {...sharedXAxisProps}
                dataKey="hour"
                interval={getResponsiveInterval()}
                tick={<CustomTick />}
                height={60}
              />
              <YAxis
                label={{
                  value: 'kWh',
                  angle: -90,
                  position: 'insideLeft',
                  offset: -30,
                }}
                fontSize={10}
                tickFormatter={(value) => value.toFixed(2)}
              />
              <Tooltip {...sharedTooltipProps} />
              {selectedMeterData.map(({ data, color, meterId }) => (
                <Bar
                  key={meterId}
                  dataKey="value"
                  name={`Total Usage (kWh)`}
                  fill={color}
                  radius={[4, 4, 0, 0]}
                  animationBegin={0}
                  animationDuration={1500}
                  animationEasing="ease-out"
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderPeriodChart = (selectedMeterData: { data: ProcessedDataPoint[]; color: string; meterId: string }[]) => {
    // Transform the data for period view
    const periodData = selectedMeterData.map((meterData) => ({
      ...meterData,
      data: processIntervalDataByPeriod(intervalData[meterData.meterId] || []),
    }));

    // Define colors for each time period
    const periodColors = {
      Morning: 'rgb(255, 183, 77)', // Warm sunrise orange
      Afternoon: 'rgb(87, 202, 244)', // Bright sky blue
      Evening: 'rgb(251, 140, 0)', // Sunset orange
      Night: 'rgb(66, 99, 235)', // Deep night blue
    };

    return (
      <div className="flex-none rounded-lg border p-2 sm:p-4">
        <h4 className="mb-2 text-base font-medium sm:mb-4">Usage by Time Period</h4>
        <div className="h-[250px] sm:h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={periodData[0]?.data || []} margin={{ top: 20, right: 30, left: 60, bottom: 70 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis {...sharedXAxisProps} dataKey="hour" interval={0} tick={{ fontSize: 12 }} height={50} />
              <YAxis
                label={{
                  value: 'kWh',
                  angle: -90,
                  position: 'insideLeft',
                  offset: -15,
                }}
                fontSize={12}
              />
              <Tooltip {...sharedTooltipProps} />
              {periodData.map(({ data, meterId }) => (
                <Bar
                  key={meterId}
                  dataKey="value"
                  radius={[4, 4, 0, 0]}
                  animationBegin={0}
                  animationDuration={1500}
                  animationEasing="ease-out"
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={periodColors[entry.hour as keyof typeof periodColors]} />
                  ))}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const calculateWeekdayWeekendUsage = (data: IntervalBlock[]) => {
    const weekdayUsage = data.reduce((sum, reading) => {
      const date = new Date(reading.startTime);
      const day = date.getDay();
      // 0 is Sunday, 6 is Saturday
      if (day >= 1 && day <= 5) {
        return sum + reading.value;
      }
      return sum;
    }, 0);

    const weekendUsage = data.reduce((sum, reading) => {
      const date = new Date(reading.startTime);
      const day = date.getDay();
      // 0 is Sunday, 6 is Saturday
      if (day === 0 || day === 6) {
        return sum + reading.value;
      }
      return sum;
    }, 0);

    return { weekdayUsage, weekendUsage };
  };

  return (
    <div className="flex h-screen flex-col overflow-y-auto">
      <div className="min-h-full flex-1 p-2 sm:p-4">
        <div className="flex flex-col gap-4 sm:gap-6">
          <h3 className="mt-0 text-base font-semibold sm:text-lg">ConEdison Energy Usage</h3>

          {/* Time Range Controls */}
          <div className="flex-none space-y-3 sm:space-y-4">
            <div className="flex flex-col items-start gap-3 sm:gap-4">
              <div className="w-full">
                <div className="flex flex-col gap-2 sm:flex-row">
                  {[...(Object.entries(TIME_RANGE_CONFIG) as [TimeRange, TimeRangeConfig][])].map(([key, config]) => (
                    <Button
                      key={key}
                      variant={timeRange === key ? 'default' : 'secondary'}
                      size="sm"
                      onClick={() => handleTimeRangeChange(key)}
                      className={`h-9 w-full whitespace-nowrap px-3 text-xs transition-all sm:w-auto sm:text-sm ${
                        timeRange === key
                          ? 'bg-primary text-primary-foreground shadow-md'
                          : 'bg-secondary hover:bg-secondary/80'
                      }`}
                    >
                      {config.label}
                    </Button>
                  ))}
                </div>
                <div className="mt-2 text-center text-[10px] text-muted-foreground sm:text-left sm:text-sm">
                  {formatDateRange()}
                </div>
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid flex-none grid-cols-1 gap-4 sm:gap-6">
            {!allMeterDataLoaded || loading ? (
              <>
                <CombinedChartSkeleton />
                <CombinedChartSkeleton />
                <InsightsSkeleton />
              </>
            ) : (
              selectedMeterIds.map((meterId) => {
                const data = intervalData[meterId] || [];
                const selectedMeterData = {
                  data: processIntervalData(data),
                  color: meterColors[meterId],
                  meterId,
                };
                return (
                  <div key={meterId} className="space-y-4 sm:space-y-6">
                    {renderHourlyChart([selectedMeterData])}
                    {renderPeriodChart([selectedMeterData])}
                  </div>
                );
              })
            )}
          </div>

          {/* Insights Section */}
          <div className="flex-none pb-8">
            {selectedMeterIds.map((meterId) => {
              const baseload = baseloads[meterId];
              const data = intervalData[meterId] || [];
              const processedData = processIntervalData(data);
              const maxUsage = processedData.length > 0 ? Math.max(...processedData.map((d) => d.value)) : 0;
              const maxHour = processedData.find((d) => d.value === maxUsage)?.hour || '';

              return (
                <div key={meterId} className="space-y-6">
                  {/* Usage Patterns Section */}
                  <div className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm">
                    <h3 className="mb-4 text-lg font-semibold text-gray-800">Usage Patterns</h3>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {/* Peak Period Card */}
                      <div className="rounded-lg bg-gradient-to-br from-blue-50 to-white p-4">
                        <div className="flex items-center gap-2">
                          <div className="rounded-full bg-blue-100 p-2">
                            <div className="h-4 w-4 rounded-full bg-muted"></div>
                          </div>
                          <h4 className="text-sm font-medium text-gray-800">Peak Period</h4>
                        </div>
                        {(() => {
                          const periodData = processIntervalDataByPeriod(data);
                          const maxPeriod =
                            periodData.length > 0
                              ? periodData.reduce((max, current) => (current.value > max.value ? current : max))
                              : { hour: '', value: 0 };
                          return (
                            <div className="mt-2">
                              <p className="text-2xl font-bold text-blue-600">{maxPeriod.hour}</p>
                              <p className="mt-1 text-sm text-gray-600">
                                Highest usage: {maxPeriod.value.toFixed(1)} kWh
                              </p>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Peak Time Card */}
                      <div className="rounded-lg bg-gradient-to-br from-orange-50 to-white p-4">
                        <div className="flex items-center gap-2">
                          <div className="rounded-full bg-orange-100 p-2">
                            <div className="h-4 w-4 rounded-full bg-muted"></div>
                          </div>
                          <h4 className="text-sm font-medium text-gray-800">Peak Time</h4>
                        </div>
                        <div className="mt-2">
                          <p className="text-2xl font-bold text-orange-600">{maxHour}</p>
                          <p className="mt-1 text-sm text-gray-600">Highest usage: {maxUsage.toFixed(1)} kWh</p>
                        </div>
                      </div>

                      {/* Off-Peak Usage Card */}
                      <div className="rounded-lg bg-gradient-to-br from-green-50 to-white p-4">
                        <div className="flex items-center gap-2">
                          <div className="rounded-full bg-green-100 p-2">
                            <div className="h-4 w-4 rounded-full bg-muted"></div>
                          </div>
                          <h4 className="text-sm font-medium text-gray-800">Off-Peak Usage</h4>
                        </div>
                        {(() => {
                          const periodData = processIntervalDataByPeriod(data);
                          const peakPeriods = ['Afternoon', 'Evening'];
                          const offPeakUsage = periodData
                            .filter((period) => !peakPeriods.includes(period.hour))
                            .reduce((sum, period) => sum + period.value, 0);
                          const totalUsage = periodData.reduce((sum, period) => sum + period.value, 0);
                          const offPeakPercentage = totalUsage > 0 ? (offPeakUsage / totalUsage) * 100 : 0;
                          return (
                            <div className="mt-2">
                              <p className="text-2xl font-bold text-green-600">{offPeakPercentage.toFixed(0)}%</p>
                              <p className="mt-1 text-sm text-gray-600">
                                {totalUsage > 0 ? 'of total consumption in off-peak hours' : 'No usage data available'}
                              </p>
                              <p className="mt-2 text-xs text-gray-500">
                                Off-peak hours: Night (10 PM - 5 AM) and Morning (5 AM - 12 PM)
                              </p>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Weekly Distribution Card - Only show for week and month views */}
                      {(timeRange === 'week' || timeRange === 'month') && (
                        <div className="rounded-lg bg-gradient-to-br from-pink-50 to-white p-4">
                          <div className="flex items-center gap-2">
                            <div className="rounded-full bg-pink-100 p-2">
                              <div className="h-4 w-4 rounded-full bg-muted"></div>
                            </div>
                            <h4 className="text-sm font-medium text-gray-800">Weekly Distribution</h4>
                          </div>
                          {(() => {
                            const { weekdayUsage, weekendUsage } = calculateWeekdayWeekendUsage(data);
                            const weekdayAvg = weekdayUsage / 5;
                            const weekendAvg = weekendUsage / 2;
                            const higherUsage = weekendAvg > weekdayAvg ? 'weekends' : 'weekdays';
                            const difference = Math.abs(weekendAvg - weekdayAvg);
                            const percentDifference = ((difference / Math.min(weekdayAvg, weekendAvg)) * 100).toFixed(
                              0
                            );

                            return (
                              <div className="mt-2">
                                <p className="text-2xl font-bold capitalize text-pink-600">{higherUsage}</p>
                                <p className="mt-1 text-sm text-gray-600">
                                  {percentDifference}% higher daily usage on {higherUsage}
                                </p>
                                <p className="mt-2 text-xs text-gray-500">
                                  Weekday avg: {weekdayAvg.toFixed(1)} kWh/day
                                  <br />
                                  Weekend avg: {weekendAvg.toFixed(1)} kWh/day
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Consumption Analysis Section */}
                  <div className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm">
                    <h3 className="mb-4 text-lg font-semibold text-gray-800">Consumption Analysis</h3>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {/* Baseload Card */}
                      <div className="rounded-lg bg-gradient-to-br from-purple-50 to-white p-4">
                        <div className="flex items-center gap-2">
                          <div className="rounded-full bg-purple-100 p-2">
                            <div className="h-4 w-4 rounded-full bg-muted"></div>
                          </div>
                          <h4 className="text-sm font-medium text-gray-800">Baseload</h4>
                        </div>
                        <div className="mt-2">
                          <p className="text-2xl font-bold text-purple-600">
                            {baseload ? baseload.toFixed(2) : '0.00'} kWh
                          </p>
                          <p className="mt-1 text-sm text-gray-600">Continuous power consumption</p>
                          <p className="mt-2 text-xs text-gray-500">
                            This represents your minimum continuous power usage, typically from always-on appliances and
                            standby power.
                          </p>
                        </div>
                      </div>

                      {/* Peak vs Average Card */}
                      <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-white p-4">
                        <div className="flex items-center gap-2">
                          <div className="rounded-full bg-indigo-100 p-2">
                            <div className="h-4 w-4 rounded-full bg-muted"></div>
                          </div>
                          <h4 className="text-sm font-medium text-gray-800">Peak vs Average</h4>
                        </div>
                        {(() => {
                          const periodData = processIntervalDataByPeriod(data);
                          const totalUsage = periodData.reduce((sum, period) => sum + period.value, 0);
                          const avgUsage = periodData.length > 0 ? totalUsage / periodData.length : 0;
                          const maxUsage = periodData.length > 0 ? Math.max(...periodData.map((p) => p.value)) : 0;
                          const ratio = avgUsage > 0 ? maxUsage / avgUsage : 0;
                          return (
                            <div className="mt-2">
                              <p className="text-2xl font-bold text-indigo-600">
                                {ratio > 0 ? `${ratio.toFixed(1)}x` : '0x'}
                              </p>
                              <p className="mt-1 text-sm text-gray-600">
                                {ratio > 0
                                  ? `Peak is ${((ratio - 1) * 100).toFixed(0)}% above average`
                                  : 'No usage data available'}
                              </p>
                              <p className="mt-2 text-xs text-gray-500">
                                A high peak-to-average ratio indicates potential for energy savings through load
                                shifting.
                              </p>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Optimization Opportunities Section */}
                  <div className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm">
                    <h3 className="mb-4 text-lg font-semibold text-gray-800">Optimization Opportunities</h3>
                    <div className="rounded-lg bg-gradient-to-br from-teal-50 to-white p-4">
                      <div className="flex items-center gap-2">
                        <div className="rounded-full bg-teal-100 p-2">
                          <div className="h-4 w-4 rounded-full bg-muted"></div>
                        </div>
                        <h4 className="text-sm font-medium text-gray-800">Potential Savings</h4>
                      </div>
                      {(() => {
                        const periodData = processIntervalDataByPeriod(data);
                        if (periodData.length === 0) {
                          return (
                            <div className="mt-2">
                              <p className="text-2xl font-bold text-teal-600">-</p>
                              <p className="mt-1 text-sm text-gray-600">No usage data available</p>
                            </div>
                          );
                        }
                        const minPeriod = periodData.reduce((min, current) =>
                          current.value < min.value ? current : min
                        );
                        const maxPeriod = periodData.reduce((max, current) =>
                          current.value > max.value ? current : max
                        );
                        const savingsPotential = maxPeriod.value - minPeriod.value;
                        return (
                          <div className="mt-2">
                            <p className="text-2xl font-bold text-teal-600">{savingsPotential.toFixed(1)} kWh</p>
                            <p className="mt-1 text-sm text-gray-600">
                              Potential savings by shifting usage from {maxPeriod.hour} to {minPeriod.hour}
                            </p>
                            <div className="mt-4 space-y-2 rounded-lg bg-white/50 p-3">
                              <h5 className="font-medium text-gray-700">Recommendations:</h5>
                              <ul className="list-inside list-disc space-y-1 text-sm text-gray-600">
                                <li>
                                  Consider scheduling high-energy activities during {minPeriod.hour.toLowerCase()} hours
                                </li>
                                <li>Use timers or smart plugs to automate device usage during off-peak periods</li>
                                <li>
                                  Identify and minimize non-essential usage during {maxPeriod.hour.toLowerCase()} hours
                                </li>
                              </ul>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
